/**
 * Optimized search hooks for Quote filters
 * Uses debouncing and $top parameter for better performance
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { getCustomerList } from "@/features/customers/api";
import { getProductSetupList } from "@/features/product-configurator/api/productsSetup";
import { getCategorySetupList } from "@/features/product-configurator/api/categorySetup";
import { getAllConfigOptions } from "@/features/product-configurator/api/configOption";
import { IConfigOption } from "@/features/product-configurator/types/configOption";

/**
 * Debounce hook
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook to search accounts with debouncing and $top limit
 */
export const useAccountSearch = (searchText: string, limit: number = 20) => {
  const debouncedSearchText = useDebounce(searchText, 300);

  return useQuery({
    queryKey: ["account-search", debouncedSearchText, limit],
    queryFn: () =>
      getCustomerList({
        current: 0,
        pageSize: limit,
        filter: debouncedSearchText
          ? `&$filter=contains(name,'${debouncedSearchText}')&$top=${limit}`
          : `&$top=${limit}`,
      }),
    enabled: true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to search products with debouncing and $top limit
 */
export const useProductSearch = (searchText: string, limit: number = 20) => {
  const debouncedSearchText = useDebounce(searchText, 300);

  return useQuery({
    queryKey: ["product-search", debouncedSearchText, limit],
    queryFn: () =>
      getProductSetupList({
        current: 0,
        pageSize: limit,
        filter: debouncedSearchText
          ? `&$filter=contains(name,'${debouncedSearchText}')&$top=${limit}`
          : `&$top=${limit}`,
      }),
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to search categories with debouncing and $top limit
 */
export const useCategorySearch = (searchText: string, limit: number = 20) => {
  const debouncedSearchText = useDebounce(searchText, 300);

  return useQuery({
    queryKey: ["category-search", debouncedSearchText, limit],
    queryFn: () =>
      getCategorySetupList({
        current: 0,
        pageSize: limit,
        filter: debouncedSearchText
          ? `&$filter=contains(dogma_name,'${debouncedSearchText}')&$top=${limit}`
          : `&$top=${limit}`,
      }),
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to search options with client-side filtering
 * Options are pre-loaded and filtered on client side for better performance
 */
export const useOptionSearch = (
  searchText: string,
  categoryId: string | null
): {
    data: { value: IConfigOption[] },
  isLoading:boolean
} => {
  const debouncedSearchText = useDebounce(searchText, 200);

  const { data: optionsData, isLoading } = useQuery({
    queryKey: ["options-all"],
    queryFn: () => getAllConfigOptions(),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  const filteredOptions = useMemo(() => {
    const allOptions = optionsData || [];

    if (!allOptions.length) {
      return [];
    }

    // Filter by category ID if selected
    let filtered = categoryId
      ? allOptions.filter((option: any) => {
          return option._dogma_category_value === categoryId;
        })
      : allOptions;

    // Filter by search text
    if (debouncedSearchText) {
      filtered = filtered.filter((option: any) =>
        option.dogma_name
          ?.toLowerCase()
          .includes(debouncedSearchText.toLowerCase())
      );
    }

    // Limit to 50 results for performance
    return filtered.slice(0, 50);
  }, [optionsData, debouncedSearchText, categoryId]);

  return {
    data: { value: filteredOptions },
    isLoading,
  };
};
